import Icon from '@ant-design/icons';
// import { ConnectButton } from '@rainbow-me/rainbowkit';
import { notification } from 'antd';
import React, { ReactNode, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { DarkModeIcon, LightModeIcon } from 'src/assets/icons';
import { ArrowRightLanguageIconComponent } from 'src/assets/icons/IconComponent';
import { Button } from 'src/components/ui/button';
import { Skeleton } from 'src/components/ui/skeleton';
import { PATHS } from 'src/constants/paths';
import { useSocket } from 'src/contexts/SocketContext';
import { desktopAddressKey, setStorageTheme } from 'src/helpers/storage';
import { useDisclosure } from 'src/hooks/useDisclosure';
import { THEME_MODE } from 'src/interfaces/theme';
import { useAuth } from 'src/libs/rainbow';
import { useVerifyToken } from 'src/services/auth/verify';
import { useAppSelector } from 'src/store';
import { setTheme } from 'src/store/actions/theme';
import AccountContent from './AccountContent';
import './mobile-menu-detail-content.scss';
import WalletContent from './WalletContent';
import MenuItem, { IMenuItem } from './menu-item';
import { UserPositionDto } from '@/src/services/order/get-position';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from 'src/components/ui/tooltip';
import { formatRoundFloorDisplay } from 'src/helpers/formatNumber';
import BigNumber from 'bignumber.js';
import { calculateTotalPNL } from 'src/helpers';
import useWindowSize from 'src/hooks/useWindowSize';
import { useAppTranslation, useLanguageSwitcher } from 'src/i18n/hooks';
import ConnectWalletButton from 'src/components/02.buttons/ConnectWalletButton';
import LinkedDesktopWallet from './LinkedDesktopWallet';
import { Tooltip as AntdTooltip } from 'antd';
import DepositModalV2 from '../DepositModalV2';
import useGetUserAddress from 'src/hooks/useGetUserAddress';

function BalanceDisplay({
  isAuthenticated,
  userBalance,
  isBalanceSocketInitialized,
  theme,
  userPositionData,
  userAssets,
  totalUnrealizedPnl,
}: {
  isAuthenticated: boolean;
  userBalance: number;
  isBalanceSocketInitialized: boolean;
  theme: THEME_MODE;
  userPositionData: UserPositionDto[] | undefined;
  userAssets: Array<{ tokenName: string; balance: number }>;
  totalUnrealizedPnl: number;
}): ReactNode {
  const [open, setOpen] = React.useState<boolean>(false);
  const { isTablet } = useWindowSize();
  const { t } = useAppTranslation('common');

  if (!isAuthenticated) {
    return null;
  }

  if (!isBalanceSocketInitialized) {
    return <Skeleton className={`w-24 h-6 bg-gray-200`} />;
  }

  const balance = userBalance > 0 ? userBalance : '0';
  const textColor = theme === THEME_MODE.DARK ? 'text-white' : 'text-black';

  const balanceWithPnl = new BigNumber(balance || 0).plus(
    calculateTotalPNL(userPositionData ?? []),
  );

  const assetItems = userAssets
    .filter((asset) => asset.balance > 0)
    .map((asset, index) => (
      <div key={index} className="flex justify-between mb-1">
        <span>{asset.tokenName}</span>
        <span className="ml-2">{formatRoundFloorDisplay(asset.balance)}</span>
      </div>
    ));

  const formatPnl = (value: number) => {
    const formattedValue = formatRoundFloorDisplay(value);
    return value >= 0 ? `+${formattedValue}` : formattedValue;
  };
  const pnlValue = totalUnrealizedPnl;
  const pnlColor = pnlValue >= 0 ? 'text-green-500' : 'text-red-500';

  const tooltipBg = theme === THEME_MODE.DARK ? 'bg-[#1f1f1f]' : 'bg-white';
  const tooltipBorder = theme === THEME_MODE.DARK ? 'border-gray-700' : 'border-gray-200';
  const tooltipText = theme === THEME_MODE.DARK ? 'text-white' : 'text-black';
  const tooltipHeaderBorder = theme === THEME_MODE.DARK ? 'border-gray-700' : 'border-gray-300';
  const tooltipMutedText = theme === THEME_MODE.DARK ? 'text-gray-400' : 'text-gray-500';
  return (
    <TooltipProvider>
      <Tooltip
        delayDuration={isTablet ? 1000 : 0}
        onOpenChange={(e) => {
          setOpen(e);
        }}
        open={open}
      >
        <TooltipTrigger
          asChild
          onClick={(e) => {
            !isTablet && e.preventDefault();
            setOpen(true);
          }}
        >
          <div className="py-[10px]">
            <h3 className={`${textColor} cursor-default`}>
              {formatRoundFloorDisplay(balanceWithPnl)} USD
            </h3>
          </div>
        </TooltipTrigger>
        <TooltipContent
          side="bottom"
          align="end"
          className={`p-3 rounded-md shadow-md border ${tooltipBg} ${tooltipBorder} min-w-[180px] z-50 ${tooltipText}`}
          sideOffset={5}
        >
          <p className={`font-medium border-b ${tooltipHeaderBorder} pb-1 mb-2`}>
            {t('data.assets')}
          </p>
          <div className="text-xs">
            {assetItems.length > 0 ? (
              <>
                {assetItems}
                <div
                  className={`flex justify-between mt-2 pt-1 border-t ${tooltipHeaderBorder} ${pnlColor}`}
                >
                  <span>{t('data.pnl')}</span>
                  <span className="ml-2">{formatPnl(pnlValue)}</span>
                </div>
              </>
            ) : (
              <p className={tooltipMutedText}>{t('data.noAssetsAvailable')}</p>
            )}
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

interface IWalletDetail {
  address: string;
  onClickItem: () => void;
  isOpen: boolean;
  userPositionData: UserPositionDto[] | undefined;
}

const MobileMenuDetail: React.FC<IWalletDetail> = ({
  address,
  onClickItem,
  isOpen,
  userPositionData,
}) => {
  const isAuth = false;
  const dispatch = useDispatch();
  const { push } = useHistory();
  const userTheme = useSelector((state: any) => state.theme);
  const account = useSelector((state: any) => state.currentAccount);
  const [isOpenWallet, setIsOpenWallet] = useState(false);
  const [isOpenAccount, setIsOpenAccount] = useState(false);
  const { isUnauthenticated, isAuthenticated, isLinkedDesktop } = useAuth();
  const currentPair = useAppSelector((state) => state.pairs.currentPair);
  const verifyTokenQuery = useVerifyToken() as { data?: { role?: string } };
  const {address: walletAddress} = useGetUserAddress()


  const { t } = useAppTranslation('navbar');
  const { currentLanguage, availableLanguages, switchLanguage, getLanguageDisplayName } =
    useLanguageSwitcher();

  const [depositModalOpen, depositModalActions] = useDisclosure();
  const { userBalance, userAssets, totalUnrealizedPnl, isBalanceSocketInitialized } = useSocket();

  const handleChangeTheme = () => {
    if (userTheme === THEME_MODE.DARK) {
      dispatch(setTheme(THEME_MODE.LIGHT));
      setStorageTheme(THEME_MODE.LIGHT.toLocaleLowerCase());
    } else {
      dispatch(setTheme(THEME_MODE.DARK));
      setStorageTheme(THEME_MODE.DARK.toLocaleLowerCase());
    }
  };

  const handleToggleWalletContent = () => {
    setIsOpenWallet(!isOpenWallet);
  };

  const handleToggleAccountContent = () => {
    setIsOpenAccount(!isOpenAccount);
  };

  const handleDisconnect = () => {
    setIsOpenAccount(false);
    onClickItem();
  };

  useEffect(() => {
    if (!isOpen) {
      setIsOpenWallet(false);
      setIsOpenAccount(false);
    }
  }, [isOpen]);

  const ListMenuAndSub = useMemo(
    () => [
      {
        label: t('Trade'),
        className: 'exchange-icon ',
        key: `${PATHS.trade()}/${currentPair?.baseToken}`,
      },
      {
        label: 'Swap',
        className: 'exchange-icon',
        key: PATHS.swap(),
      },
      {
        label: t('VMM'),
        className: 'exchange-icon',
        key: PATHS.liquidityPool(),
      },
      {
        label: t('Portfolio'),
        key: PATHS.portfolio(),
        className: 'exchange-icon ',
      },
      {
        label: t('Referrals'),
        key: PATHS.referrals(),
      },
      {
        label: t('leaderboard', {
          ns: 'leaderboard',
        }),
        key: PATHS.tradingLeaderboard(),
        isNew: true,
      },
      ...(walletAddress && verifyTokenQuery.data?.role === 'ADMIN'
        ? [
            {
              label: t('Admin'),
              className: 'exchange-icon',
              key: PATHS.admin(),
            },
          ]
        : []),
      {
        label: t('More'),
        key: 'more',
        id: 'more-menu',
        subMenus: [
          {
            label: t('Announcements'),
            key: PATHS.announcements(),
          },
          {
            label: 'Telegram',
            link: 'https://t.me/VDEX_Community',
          },
          {
            label: 'Discord',
            link: 'https://discord.com/invite/vdex-trade',
          },
          {
            label: t('Docs'),
            link: 'https://docs.vdex.trade/',
          },
        ],
      },
    ],
    [isAuthenticated, t],
  );

  const handleDepositModal = () => {
    if (isUnauthenticated) {
      notification.info({
        message: t('message'),
        description: t('description'),
        placement: 'bottomRight',
        style: { fontFamily: 'Poppins' },
      });
    } else {
      depositModalActions.open();
    }
  };

  if (isOpenWallet) {
    return (
      <WalletContent
        address={address}
        onClose={handleToggleWalletContent}
        onClickItem={() => {
          onClickItem();
          setIsOpenWallet(false);
        }}
      />
    );
  }

  if (isOpenAccount) {
    return <AccountContent onClose={handleToggleAccountContent} onDisconnect={handleDisconnect} />;
  }

  const mobileMenuContent = ListMenuAndSub.map((item) => {
    return (
      <MenuItem
        key={item.id}
        data={item as IMenuItem}
        onClick={(key) => {
          push(key);
          onClickItem();
        }}
      />
    );
  });

  return (
    <div className="mobile-menu-detail dark:bg-dark-1 dark:text-white h-screen flex flex-col">
      <div className="flex-shrink-0">
        <div className="mt-2 dark:bg-dark-1 dark:text-white">
          <div className="flex items-center justify-center dark:bg-dark-1">
            <div className="flex items-center justify-center gap-2 flex-wrap">
              {isAuthenticated && isLinkedDesktop ? (
                <AntdTooltip title={t('wallet.depositAndWithdrawIsDisabled', { ns: 'common' })}>
                  <div>
                    <Button
                      disabled
                      id="deposit-button"
                      className="bg-[#00632B] py-4 min-w-[120px] text-white hover:bg-[#00682B]"
                      onClick={handleDepositModal}
                    >
                      {t('Deposit')}
                    </Button>
                  </div>
                </AntdTooltip>
              ) : (
                <Button
                  id="deposit-button"
                  className="bg-[#00632B] py-4 min-w-[120px] text-white hover:bg-[#00682B]"
                  onClick={handleDepositModal}
                >
                  {t('Deposit')}
                </Button>
              )}
              {isAuthenticated && isLinkedDesktop ? (
                <LinkedDesktopWallet />
              ) : (
                <ConnectWalletButton />
              )}
              <BalanceDisplay
                isAuthenticated={isAuthenticated}
                userBalance={userBalance}
                isBalanceSocketInitialized={isBalanceSocketInitialized}
                theme={userTheme}
                userAssets={userAssets}
                userPositionData={userPositionData}
                totalUnrealizedPnl={totalUnrealizedPnl}
              />
            </div>
          </div>
        </div>
        <div className="line" style={{ marginBottom: 10 }}></div>
      </div>

      <div className="flex-1 overflow-y-auto overscroll-contain">
        <div className="flex px-3.5 flex-col gap-2">{mobileMenuContent}</div>
        <div className="line"></div>

        <div className="mobile-menu-detail__item-wrapper mb-26">
          <div className="justify-between mobile-menu-detail__item-wrapper__item">
            <div>
              <span>{t('Appearance')}</span>
            </div>

            <div className="flex gap-1.5 dark:text-white">
              <span className="dark:text-white">
                {userTheme === THEME_MODE.DARK ? t('Dark') : t('Light')}
              </span>
              <img
                src={userTheme === THEME_MODE.DARK ? DarkModeIcon : LightModeIcon}
                alt="icon"
                onClick={handleChangeTheme}
              />
            </div>
          </div>
        </div>

        {/* Language Selection Section */}
        <div className="mobile-menu-detail__item-wrapper mb-26">
          {isAuthenticated && (
            <>
              <div className="mobile-menu-detail__item-wrapper__item">
                <div>
                  <span>{t('Language')}</span>
                </div>
              </div>
              {/* Language Options */}
              {availableLanguages.map((language, index) => (
                <div
                  key={language}
                  className="mobile-menu-detail__item-wrapper__item default-item ml-4"
                  onClick={() => switchLanguage(language)}
                  style={{
                    cursor: 'pointer',
                    marginBottom: '7px',
                    paddingTop: '8px',
                    paddingBottom: '8px',
                  }}
                >
                  <div className="flex items-center justify-between w-full">
                    <span
                      className={
                        currentLanguage === language ? 'selected-language-item' : 'language-item'
                      }
                      style={{
                        color:
                          currentLanguage === language
                            ? userTheme === THEME_MODE.DARK
                              ? '#ffffff'
                              : '#000000'
                            : userTheme === THEME_MODE.DARK
                              ? '#9ca3af'
                              : '#6b7280',
                        fontWeight: currentLanguage === language ? '500' : '400',
                        fontSize: '14px',
                      }}
                    >
                      {getLanguageDisplayName(language)}
                    </span>
                    {currentLanguage === language && (
                      <span
                        className="check-icon"
                        style={{
                          color: userTheme === THEME_MODE.DARK ? '#ffffff' : '#000000',
                          fontSize: '12px',
                          fontWeight: 'bold',
                        }}
                      >
                        ✓
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </>
          )}
        </div>
      </div>

      {isAuth && account && (
        <>
          <div className="mobile-menu-detail__item-wrapper">
            <div
              className="justify-between mobile-menu-detail__item-wrapper__item"
              onClick={handleToggleWalletContent}
            >
              <div>
                <span>{t('Wallet')}</span>
              </div>
              <Icon className="" component={ArrowRightLanguageIconComponent} />
            </div>
          </div>
        </>
      )}

      <div className="flex-shrink-0">
        <DepositModalV2
          key={2}
          open={depositModalOpen}
          onClose={depositModalActions.close}
          onDepositSettle={depositModalActions.close}
        />
      </div>
    </div>
  );
};

export default MobileMenuDetail;
